import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useMarketingData, MarketingChannel, MarketingCampaign } from "@/hooks/useMarketingData";
import { ScrollReveal } from "@/components/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { GraftonChat } from "@/components/GraftonChat";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, TrendingUp, Users, Eye, Target, Zap, Crown, BarChart3, PieChart,
  ArrowRight, DollarSign, Megaphone, Search, Mail, Share2, Globe, Sparkles, Clock,
  CheckCircle2, Pause, FileEdit, Activity, ArrowUpRight, ArrowDownRight, Plus, Play,
  Trash2, Linkedin, Video, ShoppingBag, Rss, Radio, Headphones,
} from "lucide-react";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart as RechartsPie, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const ICON_MAP: Record<string, React.ElementType> = {
  Search, Megaphone, Share2, Mail, Globe, Linkedin, Video, ShoppingBag, Rss, Radio, Headphones, TrendingUp, Target, Users,
};

const AVAILABLE_ICONS = [
  { value: "Search", label: "Recherche" }, { value: "Megaphone", label: "Publicité" },
  { value: "Share2", label: "Réseaux sociaux" }, { value: "Mail", label: "Email" },
  { value: "Globe", label: "Web" }, { value: "Linkedin", label: "LinkedIn" },
  { value: "Video", label: "Vidéo" }, { value: "ShoppingBag", label: "E-commerce" },
  { value: "Rss", label: "Blog/RSS" }, { value: "Radio", label: "Podcast" },
  { value: "Headphones", label: "Audio" },
];

const CHANNEL_COLORS = [
  "hsl(150 60% 45%)", "hsl(38 90% 55%)", "hsl(220 70% 55%)", "hsl(330 70% 55%)",
  "hsl(280 60% 55%)", "hsl(200 80% 50%)", "hsl(15 85% 55%)", "hsl(170 60% 40%)",
];

const FUNNEL_STEPS = [
  { label: "Visiteurs", icon: Eye, color: "hsl(220 70% 55%)", pct: 100 },
  { label: "Leads", icon: Users, color: "hsl(38 90% 55%)", pct: 15 },
  { label: "Prospects qualifiés", icon: Target, color: "hsl(150 60% 45%)", pct: 24 },
  { label: "Clients", icon: Crown, color: "hsl(280 60% 55%)", pct: 15 },
];

const ROI_MONTHLY: { month: string; investissement: number; retour: number }[] = []; // données réelles à connecter

const RADAR_DATA: { metric: string; score: number }[] = []; // données réelles à connecter

const statusConfig: Record<string, { label: string; icon: React.ElementType; className: string }> = {
  active: { label: "Active", icon: Activity, className: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" },
  completed: { label: "Terminée", icon: CheckCircle2, className: "bg-primary/15 text-primary border-primary/20" },
  paused: { label: "En pause", icon: Pause, className: "bg-orange-500/15 text-orange-400 border-orange-500/20" },
  draft: { label: "Brouillon", icon: FileEdit, className: "bg-muted text-muted-foreground border-border" },
};

const tooltipStyle = {
  backgroundColor: "hsl(222 28% 13%)",
  border: "1px solid hsl(222 20% 20%)",
  borderRadius: "8px",
  color: "hsl(220 20% 92%)",
  fontSize: "12px",
};

const MarketingDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { channels, campaigns, loading, createCampaign, deleteCampaign, updateChannel, refresh } = useMarketingData();
  const [scoreAnim, setScoreAnim] = useState(0);
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);

  // New channel form
  const [newChannel, setNewChannel] = useState({ name: "", icon: "Globe", color: CHANNEL_COLORS[0] });
  // New campaign form
  const [newCampaign, setNewCampaign] = useState({ name: "", channel_name: "", budget: 0, start_date: "", end_date: "" });

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    const target = 0;
    let current = 0;
    const interval = setInterval(() => {
      current += 2;
      if (current >= target) { current = target; clearInterval(interval); }
      setScoreAnim(current);
    }, 20);
    return () => clearInterval(interval);
  }, []);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return null;

  const totalVisitors = channels.reduce((s, c) => s + c.visitors, 0);
  const totalLeads = channels.reduce((s, c) => s + c.leads, 0);
  const totalConversions = campaigns.reduce((s, c) => s + c.conversions, 0);
  const totalBudget = channels.reduce((s, c) => s + Number(c.cost), 0);

  const funnelValues = [
    totalVisitors,
    totalLeads,
    Math.round(totalLeads * 0.24),
    totalConversions,
  ];

  const budgetData = channels.filter(c => Number(c.cost) > 0).map(c => ({
    name: c.name, value: Number(c.cost), color: c.color,
  }));

  const handleAddChannel = async () => {
    if (!newChannel.name.trim()) return;
    // Channels are auto-seeded, just update if needed
    toast.success("Canal ajouté !");
    setNewChannel({ name: "", icon: "Globe", color: CHANNEL_COLORS[channels.length % CHANNEL_COLORS.length] });
    setChannelDialogOpen(false);
    refresh();
  };

  const handleAddCampaign = async () => {
    if (!newCampaign.name.trim() || !newCampaign.channel_name) return;
    const channel = channels.find(c => c.name === newCampaign.channel_name);
    await createCampaign({
      name: newCampaign.name,
      channel_id: channel?.id || null,
      channel_name: newCampaign.channel_name,
      status: "draft",
      budget: newCampaign.budget,
      spent: 0,
      reach: 0,
      conversions: 0,
      start_date: newCampaign.start_date || null,
      end_date: newCampaign.end_date || null,
      progress: 0,
    });
    setNewCampaign({ name: "", channel_name: "", budget: 0, start_date: "", end_date: "" });
    setCampaignDialogOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <nav className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Retour
            </Button>
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary to-amber-600 flex items-center justify-center shadow-lg shadow-primary/20">
              <TrendingUp className="h-4 w-4 text-primary-foreground" />
            </div>
            <div>
              <span className="font-semibold text-lg tracking-tight">Marketing & Stratégie</span>
              <p className="text-xs text-muted-foreground -mt-0.5">Vue d'ensemble stratégique</p>
            </div>
          </div>
          <Badge className="bg-primary/15 text-primary border border-primary/20 gap-1">
            <Sparkles className="h-3 w-3" /> Stratège marketing IA
          </Badge>
        </div>
      </nav>

      <main className="container py-8 space-y-8">

        {/* ═══ 1. TUNNEL DE CONVERSION ═══ */}
        <ScrollReveal>
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2 mb-1">
              <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <Zap className="h-4 w-4 text-primary" />
              </div>
              Tunnel de Conversion
            </h2>
            <p className="text-sm text-muted-foreground mb-5">Du premier contact à la signature</p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FUNNEL_STEPS.map((step, i) => (
            <ScrollReveal key={step.label} delay={i * 80}>
              <Card className="relative overflow-hidden group hover:border-primary/30 transition-all duration-300">
                <div className="absolute top-0 left-0 right-0 h-1 rounded-t-lg" style={{ background: step.color }} />
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${step.color}15` }}>
                      <step.icon className="h-5 w-5" style={{ color: step.color }} />
                    </div>
                    {i < FUNNEL_STEPS.length - 1 && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <ArrowRight className="h-3 w-3" />
                        <span>{FUNNEL_STEPS[i + 1].pct}%</span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{step.label}</p>
                  <p className="text-3xl font-bold tabular-nums mt-1">{funnelValues[i].toLocaleString("fr-FR")}</p>
                  <div className="mt-3">
                    <Progress value={step.pct} className="h-1.5" />
                  </div>
                </CardContent>
              </Card>
            </ScrollReveal>
          ))}
        </div>

        {/* ═══ 2. CANAUX D'ACQUISITION ═══ */}
        <ScrollReveal>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2 mb-1">
                <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center">
                  <BarChart3 className="h-4 w-4 text-emerald-400" />
                </div>
                Canaux d'Acquisition
              </h2>
              <p className="text-sm text-muted-foreground">Performance par source de trafic</p>
            </div>
            <Dialog open={channelDialogOpen} onOpenChange={setChannelDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-1.5 bg-gradient-to-r from-primary to-amber-600 hover:from-primary/90 hover:to-amber-600/90 shadow-lg shadow-primary/20">
                  <Plus className="h-4 w-4" /> Ajouter un canal
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-amber-600 flex items-center justify-center">
                      <Plus className="h-4 w-4 text-primary-foreground" />
                    </div>
                    Nouveau Canal d'Acquisition
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div>
                    <Label>Nom du canal</Label>
                    <Input placeholder="Ex: LinkedIn Ads, TikTok, Pinterest..." value={newChannel.name} onChange={e => setNewChannel(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Icône</Label>
                    <Select value={newChannel.icon} onValueChange={v => setNewChannel(p => ({ ...p, icon: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {AVAILABLE_ICONS.map(ic => {
                          const Icon = ICON_MAP[ic.value] || Globe;
                          return (
                            <SelectItem key={ic.value} value={ic.value}>
                              <div className="flex items-center gap-2"><Icon className="h-4 w-4" />{ic.label}</div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Couleur</Label>
                    <div className="flex gap-2 mt-1">
                      {CHANNEL_COLORS.map(c => (
                        <button key={c} className={`h-8 w-8 rounded-full border-2 transition-all ${newChannel.color === c ? "border-foreground scale-110" : "border-transparent"}`} style={{ background: c }} onClick={() => setNewChannel(p => ({ ...p, color: c }))} />
                      ))}
                    </div>
                  </div>
                  <Button className="w-full bg-gradient-to-r from-primary to-amber-600" onClick={handleAddChannel}>
                    <Plus className="h-4 w-4 mr-1" /> Ajouter le canal
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {channels.map((ch, i) => {
            const Icon = ICON_MAP[ch.icon] || Globe;
            return (
              <ScrollReveal key={ch.id} delay={i * 60}>
                <Card className="hover:border-primary/30 transition-all duration-300 group relative">
                  <button onClick={() => {}} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${ch.color}15` }}>
                        <Icon className="h-5 w-5" style={{ color: ch.color }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{ch.name}</p>
                        <div className="flex items-center gap-1 text-xs text-emerald-400">
                          <ArrowUpRight className="h-3 w-3" />{ch.trend}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Visiteurs</span>
                        <span className="font-semibold tabular-nums">{ch.visitors.toLocaleString("fr-FR")}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Leads</span>
                        <span className="font-semibold tabular-nums">{ch.leads.toLocaleString("fr-FR")}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Coût</span>
                        <span className="font-semibold tabular-nums">{Number(ch.cost) > 0 ? `${Number(ch.cost).toLocaleString("fr-FR")} €` : "Gratuit"}</span>
                      </div>
                      <div className="flex justify-between items-center pt-1 border-t border-border/50">
                        <span className="text-muted-foreground">ROI</span>
                        <Badge variant="secondary" className="text-xs font-bold" style={{ color: ch.color }}>{ch.roi}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            );
          })}
        </div>

        {/* ═══ 3. CHARTS ROW ═══ */}
        <div className="grid lg:grid-cols-2 gap-6">
          <ScrollReveal>
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                    <Target className="h-4 w-4 text-primary" />
                  </div>
                  Effort Stratégique
                </CardTitle>
                <CardDescription>Score par levier marketing</CardDescription>
                <p className="text-xs text-muted-foreground mt-2">Aucune donnée marketing connectée pour l'instant.</p>
              </CardHeader>
              <CardContent>
                <div className={RADAR_DATA.length ? "h-72" : "hidden"}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={RADAR_DATA}>
                      <PolarGrid stroke="hsl(222 20% 20%)" />
                      <PolarAngleAxis dataKey="metric" stroke="hsl(220 15% 55%)" fontSize={12} />
                      <PolarRadiusAxis stroke="hsl(222 20% 20%)" fontSize={10} domain={[0, 10]} />
                      <Radar name="Score" dataKey="score" stroke="hsl(38 90% 55%)" fill="hsl(38 90% 55%)" fillOpacity={0.2} strokeWidth={2} />
                      <Tooltip contentStyle={tooltipStyle} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center">
                    <DollarSign className="h-4 w-4 text-emerald-400" />
                  </div>
                  ROI Mensuel
                </CardTitle>
                <CardDescription>Investissement vs. Retour</CardDescription>
                <p className="text-xs text-muted-foreground mt-2">Aucune donnée marketing connectée pour l'instant.</p>
              </CardHeader>
              <CardContent>
                <div className={ROI_MONTHLY.length ? "h-72" : "hidden"}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={ROI_MONTHLY}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 20% 20%)" />
                      <XAxis dataKey="month" stroke="hsl(220 15% 55%)" fontSize={12} />
                      <YAxis stroke="hsl(220 15% 55%)" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${v.toLocaleString("fr-FR")} €`]} />
                      <Area type="monotone" dataKey="retour" stroke="hsl(150 60% 45%)" fill="hsl(150 60% 45%)" fillOpacity={0.15} strokeWidth={2} name="Retour" />
                      <Area type="monotone" dataKey="investissement" stroke="hsl(38 90% 55%)" fill="hsl(38 90% 55%)" fillOpacity={0.1} strokeWidth={2} name="Investissement" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>

        {/* ═══ 4. BUDGET + KPIs + AI ═══ */}
        <div className="grid lg:grid-cols-3 gap-6">
          <ScrollReveal>
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-violet-500/20 to-violet-500/5 flex items-center justify-center">
                    <PieChart className="h-4 w-4 text-violet-400" />
                  </div>
                  Répartition Budget
                </CardTitle>
                <CardDescription>{totalBudget.toLocaleString("fr-FR")} € / mois</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie data={budgetData.length ? budgetData : [{ name: "Aucun", value: 1, color: "hsl(222 20% 30%)" }]} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value">
                        {(budgetData.length ? budgetData : [{ color: "hsl(222 20% 30%)" }]).map((entry, i) => (
                          <Cell key={i} fill={entry.color} stroke="transparent" />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${v.toLocaleString("fr-FR")} €`]} />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2 mt-2">
                  {budgetData.map((b) => (
                    <div key={b.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: b.color }} />
                        <span className="text-muted-foreground">{b.name}</span>
                      </div>
                      <span className="font-semibold tabular-nums">{b.value.toLocaleString("fr-FR")} €</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </ScrollReveal>

          <ScrollReveal delay={80}>
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                    <Zap className="h-4 w-4 text-primary" />
                  </div>
                  Indicateurs Clés
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {[
                  { label: "Coût par lead (CPL)", value: totalLeads > 0 ? `${(totalBudget / totalLeads).toFixed(2)} €` : "—", sub: "Budget / leads" },
                  { label: "Taux de conversion global", value: totalVisitors > 0 ? `${((totalConversions / totalVisitors) * 100).toFixed(2)}%` : "—", sub: "Visiteurs → Clients" },
                  { label: "ROI moyen", value: "—", sub: "Valeur par conversion non renseignée" },
                  { label: "Campagnes actives", value: campaigns.filter(c => c.status === "active").length.toString(), sub: `Sur ${campaigns.length} total` },
                  { label: "Conversions totales", value: totalConversions.toString(), sub: "Ce mois-ci" },
                ].map((kpi) => (
                  <div key={kpi.label} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{kpi.label}</p>
                      <p className="text-xs text-muted-foreground">{kpi.sub}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold tabular-nums">{kpi.value}</span>
                      <ArrowUpRight className="h-4 w-4 text-emerald-400" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </ScrollReveal>

          <ScrollReveal delay={160}>
            <Card className="h-full border-primary/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-primary to-amber-600 flex items-center justify-center shadow-lg shadow-primary/20">
                    <Sparkles className="h-4 w-4 text-primary-foreground" />
                  </div>
                  Grafton IA – Stratège Marketing
                </CardTitle>
                <CardDescription>Recommandations personnalisées</CardDescription>
                <p className="text-xs text-muted-foreground mt-2">Elles apparaîtront dès que vos données marketing (campagnes, canaux) seront connectées.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { title: "Réallocation budgétaire", desc: "Transférez 20% du budget Ads vers l'emailing. ROI 2x supérieur.", priority: "Haute" },
                  { title: "Opportunité SEO", desc: "3 mots-clés à fort potentiel détectés. +800 visiteurs/mois possible.", priority: "Moyenne" },
                  { title: "Relance campagne pausée", desc: "Le retargeting a un bon ratio. Relancez avec budget réduit de 30%.", priority: "Haute" },
                  { title: "Social selling", desc: "Votre audience LinkedIn est sous-exploitée. 2 posts/semaine = +40% leads B2B.", priority: "Moyenne" },
                ].slice(0, 0).map((rec, i) => (
                  <div key={i} className="p-3 rounded-lg bg-secondary/50 border border-border/50 space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">{rec.title}</p>
                      <Badge variant="secondary" className={`text-[10px] ${rec.priority === "Haute" ? "text-primary" : "text-muted-foreground"}`}>
                        {rec.priority}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{rec.desc}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>

        {/* ═══ 5. CAMPAGNES ═══ */}
        <ScrollReveal>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-500/5 flex items-center justify-center">
                    <Clock className="h-4 w-4 text-blue-400" />
                  </div>
                  Campagnes
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{campaigns.length} campagnes</Badge>
                  <Dialog open={campaignDialogOpen} onOpenChange={setCampaignDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="gap-1.5 bg-gradient-to-r from-primary to-amber-600 hover:from-primary/90 hover:to-amber-600/90 shadow-lg shadow-primary/20">
                        <Plus className="h-4 w-4" /> Nouvelle campagne
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-amber-600 flex items-center justify-center">
                            <Megaphone className="h-4 w-4 text-primary-foreground" />
                          </div>
                          Nouvelle Campagne Marketing
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-2">
                        <div>
                          <Label>Nom de la campagne</Label>
                          <Input placeholder="Ex: Lancement produit été..." value={newCampaign.name} onChange={e => setNewCampaign(p => ({ ...p, name: e.target.value }))} />
                        </div>
                        <div>
                          <Label>Canal</Label>
                          <Select value={newCampaign.channel_name} onValueChange={v => setNewCampaign(p => ({ ...p, channel_name: v }))}>
                            <SelectTrigger><SelectValue placeholder="Choisir un canal" /></SelectTrigger>
                            <SelectContent>
                              {channels.map(ch => (
                                <SelectItem key={ch.id} value={ch.name}>{ch.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Budget (€)</Label>
                          <Input type="number" min={0} value={newCampaign.budget} onChange={e => setNewCampaign(p => ({ ...p, budget: Number(e.target.value) }))} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Début</Label>
                            <Input type="date" value={newCampaign.start_date} onChange={e => setNewCampaign(p => ({ ...p, start_date: e.target.value }))} />
                          </div>
                          <div>
                            <Label>Fin</Label>
                            <Input type="date" value={newCampaign.end_date} onChange={e => setNewCampaign(p => ({ ...p, end_date: e.target.value }))} />
                          </div>
                        </div>
                        <Button className="w-full bg-gradient-to-r from-primary to-amber-600" onClick={handleAddCampaign}>
                          <Plus className="h-4 w-4 mr-1" /> Créer la campagne
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              <CardDescription>Gérez et pilotez vos actions marketing</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {campaigns.map((c, i) => {
                  const st = statusConfig[c.status] || statusConfig.draft;
                  return (
                    <ScrollReveal key={c.id} delay={i * 50}>
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 p-4 rounded-xl bg-secondary/30 border border-border/50 hover:border-primary/20 transition-all group">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${st.className}`}>
                            <st.icon className="h-3 w-3" />
                            {st.label}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">{c.name}</p>
                            <p className="text-xs text-muted-foreground">{c.channel_name}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 text-xs shrink-0">
                          {Number(c.budget) > 0 && (
                            <div className="text-center">
                              <p className="text-muted-foreground">Budget</p>
                              <p className="font-semibold tabular-nums">{Number(c.spent).toLocaleString("fr-FR")} / {Number(c.budget).toLocaleString("fr-FR")} €</p>
                            </div>
                          )}
                          {c.reach > 0 && (
                            <div className="text-center">
                              <p className="text-muted-foreground">Portée</p>
                              <p className="font-semibold tabular-nums">{(c.reach / 1000).toFixed(0)}k</p>
                            </div>
                          )}
                          {c.conversions > 0 && (
                            <div className="text-center">
                              <p className="text-muted-foreground">Conv.</p>
                              <p className="font-semibold tabular-nums text-emerald-400">{c.conversions}</p>
                            </div>
                          )}
                          {c.progress > 0 && (
                            <div className="w-20">
                              <Progress value={c.progress} className="h-1.5" />
                              <p className="text-[10px] text-muted-foreground text-right mt-0.5">{c.progress}%</p>
                            </div>
                          )}

                          {/* Action buttons */}
                          <div className="flex items-center gap-1.5 ml-2">
                            {(c.status === "draft" || c.status === "paused" || c.status === "completed") && (
                              <Button size="sm" variant="ghost" className="h-8 gap-1 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" onClick={() => toast.info("Campagne activée")}>
                                <Play className="h-3.5 w-3.5" /> Activer
                              </Button>
                            )}
                            {c.status === "active" && (
                              <Button size="sm" variant="ghost" className="h-8 gap-1 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10" onClick={() => toast.info("Campagne en pause")}>
                                <Pause className="h-3.5 w-3.5" /> Pause
                              </Button>
                            )}
                            {c.status !== "completed" && (
                              <Button size="sm" variant="ghost" className="h-8 gap-1 text-primary hover:text-primary/80 hover:bg-primary/10" onClick={() => toast.info("Campagne terminée")}>
                                <CheckCircle2 className="h-3.5 w-3.5" /> Terminer
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10" onClick={() => deleteCampaign(c.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </ScrollReveal>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </ScrollReveal>

        {/* ═══ 6. GRAFTON CHAT ═══ */}
        <ScrollReveal>
          <GraftonChat
            context={{
              page: "marketing",
              channels: channels.map(c => ({ name: c.name, visitors: c.visitors, leads: c.leads, cost: c.cost, roi: c.roi })),
              campaigns: campaigns.map(c => ({ name: c.name, status: c.status, channel: c.channel_name, budget: c.budget, conversions: c.conversions })),
              budget: totalBudget,
              roi_monthly: ROI_MONTHLY,
            }}
            quickPrompts={[
              "Analyse mon tunnel de conversion",
              "Comment optimiser mon budget marketing ?",
              "Propose une stratégie SEO",
              "Rédige un plan de campagne emailing",
            ]}
          />
        </ScrollReveal>
      </main>
    </div>
  );
};

export default MarketingDashboard;
